/**
 * Feature-Flags / Migrationsschalter Auftragsverwaltung laut AV-013 (#6671).
 * Referenz: docs/auftragsverwaltung/AV-013-feature-flags-migrationsschalter-rollout.md
 */

export const ORDER_MANAGEMENT_ROLLOUT_VERSION = '1.1.0';

export const ORDER_MANAGEMENT_ROLLOUT_SECTION_KEY = 'orderManagementRollout';

export enum OrderManagementRolloutEnvironmentOverride {
    None = 'none',
    ForceLegacy = 'force_legacy',
    ForceV2 = 'force_v2',
}

export enum OrderManagementRolloutFlag {
    OrderFormV2 = 'orderFormV2',
    AuditHistoryTab = 'auditHistoryTab',
    OfflineSyncV2 = 'offlineSyncV2',
    BillingTransferV2 = 'billingTransferV2',
    TimeTrackingV2 = 'timeTrackingV2',
    ContractValidationV2 = 'contractValidationV2',
    DockSiteInfoSheetV2 = 'dockSiteInfoSheetV2',
    OrderListV2 = 'orderListV2',
    OrderDetailV2 = 'orderDetailV2',
    OrderAttachmentsTab = 'orderAttachmentsTab',
    OrderConfirmations = 'orderConfirmations',
    OrderQuotes = 'orderQuotes',
}

export enum OrderManagementRolloutResolutionSource {
    Default = 'default',
    Tenant = 'tenant',
    Environment = 'environment',
}

export interface OrderManagementRolloutFlagEntry {
    enabled: boolean;
    activatedUntil: string | null;
    disabledTemporarily: boolean;
    disabledReason: string | null;
}

export interface OrderManagementRolloutTenantSettings {
    orderFormV2?: boolean | string | OrderManagementRolloutFlagEntry;
    auditHistoryTab?: boolean | string | OrderManagementRolloutFlagEntry;
    offlineSyncV2?: boolean | string | OrderManagementRolloutFlagEntry;
    billingTransferV2?: boolean | string | OrderManagementRolloutFlagEntry;
    timeTrackingV2?: boolean | string | OrderManagementRolloutFlagEntry;
    contractValidationV2?: boolean | string | OrderManagementRolloutFlagEntry;
    dockSiteInfoSheetV2?: boolean | string | OrderManagementRolloutFlagEntry;
    orderListV2?: boolean | string | OrderManagementRolloutFlagEntry;
    orderDetailV2?: boolean | string | OrderManagementRolloutFlagEntry;
    orderAttachmentsTab?: boolean | string | OrderManagementRolloutFlagEntry;
    orderConfirmations?: boolean | string | OrderManagementRolloutFlagEntry;
    orderQuotes?: boolean | string | OrderManagementRolloutFlagEntry;
}

export interface OrderManagementRolloutEnvironmentConfig {
    override?: OrderManagementRolloutEnvironmentOverride | string | null;
    forcedFlags?: Partial<Record<OrderManagementRolloutFlag, boolean>> | null;
}

export interface OrderManagementRolloutResolution {
    flag: OrderManagementRolloutFlag;
    useV2Flow: boolean;
    useLegacyFlow: boolean;
    source: OrderManagementRolloutResolutionSource;
}

export interface OrderManagementRolloutResolutionContext {
    activeModulePermissionNames?: string[] | null;
}

export type OrderManagementRolloutGroupKey = 'bcm_orders' | 'docksite' | 'working_time' | 'contracts';

export interface OrderManagementRolloutFlagDefinition {
    flag: OrderManagementRolloutFlag;
    groupKey: OrderManagementRolloutGroupKey;
    parentModulePermissionNames: string[];
}

/** Modulrecht für BCM-Web-Auftrags-Rollouts (Menü „Aufträge“, Route /orders). */
export const ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES = ['docksite'];

/**
 * Rollout „Zeiten am Auftrag“ – bewusst ohne `working_time_management` (BCM-Arbeitszeitmodul /working-time).
 */
export const ORDER_MANAGEMENT_ROLLOUT_TIME_TRACKING_PARENT_MODULE_PERMISSION_NAMES = [
    ...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES,
    'employees',
];

const ALL_ROLLOUT_FLAGS: OrderManagementRolloutFlag[] = [
    OrderManagementRolloutFlag.OrderFormV2,
    OrderManagementRolloutFlag.AuditHistoryTab,
    OrderManagementRolloutFlag.OfflineSyncV2,
    OrderManagementRolloutFlag.BillingTransferV2,
    OrderManagementRolloutFlag.TimeTrackingV2,
    OrderManagementRolloutFlag.ContractValidationV2,
    OrderManagementRolloutFlag.DockSiteInfoSheetV2,
    OrderManagementRolloutFlag.OrderListV2,
    OrderManagementRolloutFlag.OrderDetailV2,
    OrderManagementRolloutFlag.OrderAttachmentsTab,
    OrderManagementRolloutFlag.OrderConfirmations,
    OrderManagementRolloutFlag.OrderQuotes,
];

export const ORDER_MANAGEMENT_ROLLOUT_FLAG_DEFINITIONS: OrderManagementRolloutFlagDefinition[] = [
    { flag: OrderManagementRolloutFlag.OrderListV2, groupKey: 'bcm_orders', parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES] },
    { flag: OrderManagementRolloutFlag.OrderFormV2, groupKey: 'bcm_orders', parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES] },
    { flag: OrderManagementRolloutFlag.OrderDetailV2, groupKey: 'bcm_orders', parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES] },
    { flag: OrderManagementRolloutFlag.AuditHistoryTab, groupKey: 'bcm_orders', parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES] },
    { flag: OrderManagementRolloutFlag.BillingTransferV2, groupKey: 'bcm_orders', parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES] },
    { flag: OrderManagementRolloutFlag.OrderAttachmentsTab, groupKey: 'bcm_orders', parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES] },
    { flag: OrderManagementRolloutFlag.OrderConfirmations, groupKey: 'bcm_orders', parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES] },
    { flag: OrderManagementRolloutFlag.OrderQuotes, groupKey: 'bcm_orders', parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES] },
    { flag: OrderManagementRolloutFlag.ContractValidationV2, groupKey: 'contracts', parentModulePermissionNames: ['contract_manager'] },
    {
        flag: OrderManagementRolloutFlag.TimeTrackingV2,
        groupKey: 'bcm_orders',
        parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_TIME_TRACKING_PARENT_MODULE_PERMISSION_NAMES],
    },
    { flag: OrderManagementRolloutFlag.OfflineSyncV2, groupKey: 'docksite', parentModulePermissionNames: ['docksite_orders'] },
    { flag: OrderManagementRolloutFlag.DockSiteInfoSheetV2, groupKey: 'docksite', parentModulePermissionNames: ['docksite_orders'] },
];

const ENV_OVERRIDE_ALIASES: Record<string, OrderManagementRolloutEnvironmentOverride> = {
    none: OrderManagementRolloutEnvironmentOverride.None,
    force_legacy: OrderManagementRolloutEnvironmentOverride.ForceLegacy,
    forcelegacy: OrderManagementRolloutEnvironmentOverride.ForceLegacy,
    legacy: OrderManagementRolloutEnvironmentOverride.ForceLegacy,
    force_v2: OrderManagementRolloutEnvironmentOverride.ForceV2,
    forcev2: OrderManagementRolloutEnvironmentOverride.ForceV2,
    v2: OrderManagementRolloutEnvironmentOverride.ForceV2,
};

function getDefaultOrderManagementRolloutFlagEntry(): OrderManagementRolloutFlagEntry {
    return {
        enabled: false,
        activatedUntil: null,
        disabledTemporarily: false,
        disabledReason: null,
    };
}

function normalizeOrderManagementRolloutFlagEntry(raw: Partial<OrderManagementRolloutFlagEntry>): OrderManagementRolloutFlagEntry {
    return {
        enabled: raw.enabled === true,
        activatedUntil: raw.activatedUntil == null || raw.activatedUntil === ''
            ? null
            : String(raw.activatedUntil),
        disabledTemporarily: raw.disabledTemporarily === true,
        disabledReason: raw.disabledReason == null || raw.disabledReason === ''
            ? null
            : String(raw.disabledReason).trim(),
    };
}

export function parseOrderManagementRolloutBool(value: unknown, fallback = false): boolean {
    if (value === true || value === 'true' || value === 1 || value === '1') {
        return true;
    }

    if (value === false || value === 'false' || value === 0 || value === '0') {
        return false;
    }

    return fallback;
}

export function parseOrderManagementRolloutFlagEntry(raw: unknown): OrderManagementRolloutFlagEntry {
    if (raw == null || raw === '') {
        return getDefaultOrderManagementRolloutFlagEntry();
    }

    if (typeof raw === 'boolean' || typeof raw === 'number') {
        return {
            ...getDefaultOrderManagementRolloutFlagEntry(),
            enabled: parseOrderManagementRolloutBool(raw),
        };
    }

    if (typeof raw === 'string') {
        const trimmed = raw.trim();

        if (trimmed.startsWith('{')) {
            try {
                return normalizeOrderManagementRolloutFlagEntry(JSON.parse(trimmed));
            } catch {
                return getDefaultOrderManagementRolloutFlagEntry();
            }
        }

        return {
            ...getDefaultOrderManagementRolloutFlagEntry(),
            enabled: parseOrderManagementRolloutBool(trimmed),
        };
    }

    if (typeof raw === 'object') {
        return normalizeOrderManagementRolloutFlagEntry(raw as Partial<OrderManagementRolloutFlagEntry>);
    }

    return getDefaultOrderManagementRolloutFlagEntry();
}

export function resolveEffectiveRolloutEnabled(
    entry: OrderManagementRolloutFlagEntry,
    now: Date = new Date(),
): boolean {
    const normalized = normalizeOrderManagementRolloutFlagEntry(entry);

    if (!normalized.enabled || normalized.disabledTemporarily) {
        return false;
    }

    if (normalized.activatedUntil) {
        const until = Date.parse(normalized.activatedUntil);

        if (!Number.isNaN(until) && until < now.getTime()) {
            return false;
        }
    }

    return true;
}

export function getDefaultOrderManagementRolloutTenantSettings(): Required<Record<OrderManagementRolloutFlag, boolean>> {
    return ALL_ROLLOUT_FLAGS.reduce((acc, flag) => {
        acc[flag] = false;
        return acc;
    }, {} as Required<Record<OrderManagementRolloutFlag, boolean>>);
}

export function parseOrderManagementRolloutTenantSettings(
    raw: unknown,
): Required<Record<OrderManagementRolloutFlag, boolean>> {
    const defaults = getDefaultOrderManagementRolloutTenantSettings();
    const section = raw && typeof raw === 'object' ? (raw as OrderManagementRolloutTenantSettings) : {};

    return ALL_ROLLOUT_FLAGS.reduce((acc, flag) => {
        acc[flag] = resolveEffectiveRolloutEnabled(parseOrderManagementRolloutFlagEntry(section[flag]));

        return acc;
    }, { ...defaults });
}

export function normalizeOrderManagementRolloutEnvironmentOverride(
    value: unknown,
): OrderManagementRolloutEnvironmentOverride {
    if (value == null || value === '') {
        return OrderManagementRolloutEnvironmentOverride.None;
    }

    const normalized = String(value).trim().toLowerCase();

    return ENV_OVERRIDE_ALIASES[normalized] || OrderManagementRolloutEnvironmentOverride.None;
}

export function parseOrderManagementRolloutEnvironmentConfig(
    raw: unknown,
): OrderManagementRolloutEnvironmentConfig {
    if (!raw || typeof raw !== 'object') {
        return { override: OrderManagementRolloutEnvironmentOverride.None, forcedFlags: null };
    }

    const config = raw as OrderManagementRolloutEnvironmentConfig;

    return {
        override: normalizeOrderManagementRolloutEnvironmentOverride(config.override),
        forcedFlags: config.forcedFlags || null,
    };
}

export function parseOrderManagementRolloutEnvironmentFromEnv(
    env: Record<string, string | undefined> = {},
): OrderManagementRolloutEnvironmentConfig {
    const override = normalizeOrderManagementRolloutEnvironmentOverride(env.U2B_ORDER_ROLLOUT_OVERRIDE);
    const forcedFlags: Partial<Record<OrderManagementRolloutFlag, boolean>> = {};

    const rawFlags = env.U2B_ORDER_ROLLOUT_FLAGS;

    if (rawFlags) {
        rawFlags.split(',').map(item => item.trim()).filter(Boolean).forEach((flagName) => {
            if ((ALL_ROLLOUT_FLAGS as string[]).includes(flagName)) {
                forcedFlags[flagName as OrderManagementRolloutFlag] = true;
            }
        });
    }

    return {
        override,
        forcedFlags: Object.keys(forcedFlags).length ? forcedFlags : null,
    };
}

function readTenantFlagValue(
    flag: OrderManagementRolloutFlag,
    tenantSettings: OrderManagementRolloutTenantSettings | null | undefined,
): boolean {
    const section = tenantSettings && typeof tenantSettings === 'object' ? tenantSettings : {};

    return resolveEffectiveRolloutEnabled(parseOrderManagementRolloutFlagEntry(section[flag]));
}

export function resolveOrderManagementRolloutFlag(
    flag: OrderManagementRolloutFlag,
    tenantSettings: OrderManagementRolloutTenantSettings | null | undefined,
    environment: OrderManagementRolloutEnvironmentConfig | null | undefined,
    context?: OrderManagementRolloutResolutionContext | null,
): OrderManagementRolloutResolution {
    const envConfig = parseOrderManagementRolloutEnvironmentConfig(environment);
    const override = envConfig.override || OrderManagementRolloutEnvironmentOverride.None;

    if (override === OrderManagementRolloutEnvironmentOverride.ForceLegacy) {
        return {
            flag,
            useV2Flow: false,
            useLegacyFlow: true,
            source: OrderManagementRolloutResolutionSource.Environment,
        };
    }

    if (override === OrderManagementRolloutEnvironmentOverride.ForceV2) {
        const forcedFlags = envConfig.forcedFlags;

        if (forcedFlags && Object.prototype.hasOwnProperty.call(forcedFlags, flag)) {
            const useV2Flow = forcedFlags[flag] === true;

            return {
                flag,
                useV2Flow,
                useLegacyFlow: !useV2Flow,
                source: OrderManagementRolloutResolutionSource.Environment,
            };
        }

        return {
            flag,
            useV2Flow: true,
            useLegacyFlow: false,
            source: OrderManagementRolloutResolutionSource.Environment,
        };
    }

    const tenantEnabled = readTenantFlagValue(flag, tenantSettings);

    if (tenantEnabled && areOrderManagementRolloutParentModulesActive(flag, context?.activeModulePermissionNames)) {
        return {
            flag,
            useV2Flow: true,
            useLegacyFlow: false,
            source: OrderManagementRolloutResolutionSource.Tenant,
        };
    }

    return {
        flag,
        useV2Flow: false,
        useLegacyFlow: true,
        source: OrderManagementRolloutResolutionSource.Default,
    };
}

export function resolveAllOrderManagementRolloutFlags(
    tenantSettings: OrderManagementRolloutTenantSettings | null | undefined,
    environment: OrderManagementRolloutEnvironmentConfig | null | undefined,
    context?: OrderManagementRolloutResolutionContext | null,
): Record<OrderManagementRolloutFlag, OrderManagementRolloutResolution> {
    return ALL_ROLLOUT_FLAGS.reduce<Record<OrderManagementRolloutFlag, OrderManagementRolloutResolution>>(
        (acc, flag) => {
            acc[flag] = resolveOrderManagementRolloutFlag(flag, tenantSettings, environment, context);

            return acc;
        },
        {} as Record<OrderManagementRolloutFlag, OrderManagementRolloutResolution>,
    );
}

export function isOrderManagementV2Enabled(
    flag: OrderManagementRolloutFlag,
    tenantSettings: OrderManagementRolloutTenantSettings | null | undefined,
    environment: OrderManagementRolloutEnvironmentConfig | null | undefined,
    context?: OrderManagementRolloutResolutionContext | null,
): boolean {
    return resolveOrderManagementRolloutFlag(flag, tenantSettings, environment, context).useV2Flow;
}

export function shouldUseLegacyOrderManagementFlow(
    flag: OrderManagementRolloutFlag,
    tenantSettings: OrderManagementRolloutTenantSettings | null | undefined,
    environment: OrderManagementRolloutEnvironmentConfig | null | undefined,
    context?: OrderManagementRolloutResolutionContext | null,
): boolean {
    return resolveOrderManagementRolloutFlag(flag, tenantSettings, environment, context).useLegacyFlow;
}

export function listOrderManagementRolloutFlags(): OrderManagementRolloutFlag[] {
    return [...ALL_ROLLOUT_FLAGS];
}

export function getOrderManagementRolloutFlagDefinition(
    flag: OrderManagementRolloutFlag,
): OrderManagementRolloutFlagDefinition {
    return ORDER_MANAGEMENT_ROLLOUT_FLAG_DEFINITIONS.find((definition) => definition.flag === flag)
        || {
            flag,
            groupKey: 'bcm_orders',
            parentModulePermissionNames: [...ORDER_MANAGEMENT_ROLLOUT_BCM_ORDERS_PARENT_MODULE_PERMISSION_NAMES],
        };
}

export function areOrderManagementRolloutParentModulesActive(
    flag: OrderManagementRolloutFlag,
    activeModulePermissionNames: Iterable<string> | null | undefined,
): boolean {
    const definition = getOrderManagementRolloutFlagDefinition(flag);

    if (!definition.parentModulePermissionNames.length) {
        return true;
    }

    const active = new Set(activeModulePermissionNames || []);

    return definition.parentModulePermissionNames.every((permissionName) => active.has(permissionName));
}
